/**
 * The workspace's transport — a floating bar near the bottom, the way a DAW's is.
 *
 * It is inset from all three edges rather than flush against the viewport bottom:
 * a full-bleed bar reads as browser chrome and, on a phone, sits underneath the
 * home indicator. Floating it with a border, a shadow and rounded corners makes it
 * legible as a control surface belonging to the workspace.
 *
 * It floats but it does not overlap. The bar is still a row of the workspace grid,
 * inset within that row by its own margin, so the columns above it are laid out in
 * the space that is left — the timeline can scroll to its last row without hiding
 * behind it. A position: fixed bar would have needed a matching bottom padding on
 * the scroll container to achieve the same thing. See .lyx-ws-transport.
 *
 * This is the drawer's player, moved: play/pause, the scrubber, elapsed and total
 * time, the waveform, repeat, volume and speed. What changed is that the transport is
 * always on screen instead of scrolling away with the panel above it, which is the
 * point of docking it.
 *
 * PLAYBACK ONLY — no download or export here. The bar used to carry a More menu with
 * Download and Open downloads. Saving a file is not a transport control: it does not
 * change what you are hearing, it is the same action the ACTIVE SONG panel's Download
 * button and its Export options menu item already perform, and a persistent bar is
 * the worst place to keep a duplicate of it. Both remain in ACTIVE SONG, and the
 * Download Center panel is unchanged — the controls moved out of this bar, the
 * functionality did not leave the Studio.
 *
 * Previous/Next are new controls but not new functionality: they select the
 * adjacent song in the library, which is the same navigation the library strip
 * does. They are disabled at the ends rather than wrapping — a wrap would make
 * "next" ambiguous in a five-track library.
 *
 * SIMULATED. There is no <audio> element: `elapsed` is advanced by an interval in
 * useSimulatedPlayback and the bars are a seeded PRNG rather than amplitude.
 *
 * The scrubber IS seekable, which is a narrower claim than it sounds: seeking moves
 * `elapsed`, and the clock, the waveform fill and the timeline playhead all follow it,
 * because they are all rendered from it. What it does not do is move an audio position,
 * since there is none.
 *
 * The SPEED selector is real in the same sense, and it was not before: `speed` is now the
 * period of that interval, so 2× genuinely halves how long the track takes and doubles
 * how fast the clock, the fill and the playhead move. It changes no pitch, there being
 * nothing to pitch. See src/lib/playback.ts.
 *
 * Volume and mute remain recorded without affecting anything audible — there is nothing
 * to silence yet — and are preserved deliberately. Neither is a fake: each drives the
 * whole of the state that exists today and is the exact place an audio element gets
 * wired in. See src/stores/playerStore.ts.
 */
import { Loader2, Maximize2, Pause, Play, Repeat, SkipBack, SkipForward, Volume2, VolumeX } from 'lucide-react';
import { IconButton, Tooltip } from '../ui';
import { SeekBar, Waveform } from '../player';
import { formatTime, parseLength } from '../../lib/formatting';
import { seededHeights } from '../../lib/waveform';
import { PLAYBACK_SPEEDS } from '../../data/music';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

export interface TransportBarProps {
  song: Song;
  totalLabel: string;
  playing: boolean;
  /**
   * True when this song is loaded, running or paused.
   *
   * The clock and the scrubber read this; the play button's label and the waveform's
   * animation read `playing`. Defaults to `playing`, so a caller with no pause state
   * behaves exactly as before.
   */
  active?: boolean;
  elapsed: number;
  volume: number;
  speed: number;
  repeat: boolean;
  /**
   * The loaded track's runtime in seconds, when the player has resolved one.
   *
   * The scrubber spans this rather than the song's declared length, so a loaded
   * VERSION — which can be shorter or longer than the song after an edit — makes the
   * bar complete on the version's runtime. Falls back to `parseLength(song.length)`
   * when absent or non-positive, so a caller that does not track it behaves as before.
   * Only consulted while `active`; an idle bar shows the song's own length. See
   * usePlayer's version-aware `durationSec` and playerStore.loadVersion.
   */
  durationSec?: number;
  /** Whether output is silenced. Independent of `volume` — see playerStore. */
  muted: boolean;
  /** True while the shared player is focused on a live Mix preview. */
  previewMode?: boolean;
  /** True while the preview-facing playback is waiting on a fresh render/load. */
  previewLoading?: boolean;
  /** Short preview-state label shown while previewMode is active. */
  previewLabel?: string;
  /** Toggles whether the transport is focused on preview or the head song. */
  onTogglePreviewMode?: () => void;
  onTogglePlay: () => void;
  onSetVolume: (volume: number) => void;
  onSetSpeed: (speed: number) => void;
  onToggleRepeat: () => void;
  /** Mutes or unmutes, restoring the previous level. */
  onToggleMute: () => void;
  /** Moves the playback position to the given second. */
  onSeek: (seconds: number) => void;
  /** Opens the full-screen player. Omitted where there is none. */
  onOpenFullscreen?: () => void;
  /** Selects the previous song. Omitted when this is the first track. */
  onPrev?: () => void;
  /** Selects the next song. Omitted when this is the last track. */
  onNext?: () => void;
}

export default function TransportBar({
  song,
  totalLabel,
  playing,
  active = playing,
  elapsed,
  volume,
  speed,
  repeat,
  muted,
  durationSec,
  previewMode = false,
  previewLoading = false,
  previewLabel,
  onTogglePreviewMode,
  onTogglePlay,
  onSetVolume,
  onSetSpeed,
  onToggleRepeat,
  onToggleMute,
  onSeek,
  onOpenFullscreen,
  onPrev,
  onNext,
}: TransportBarProps) {
  // The scrubber's span. The version-aware runtime wins while this song is the loaded
  // one — so the bar completes on a loaded version's length — and the song's declared
  // length is the fallback for an idle bar or a caller that does not track it. The
  // `> 0` guards a zero from an unresolved duration, which would collapse the scrubber.
  const totalSec = active && durationSec && durationSec > 0 ? durationSec : parseLength(song.length);
  // Position is only meaningful while this song is the loaded one — the player store
  // holds a single `elapsed` for whatever is active — but a pause does not end that.
  // Reading `playing` here would reset the clock to 0:00 on pause and jump it back on
  // resume, while the store had held the position all along.
  const pos = active ? elapsed : 0;

  return (
    <div
      className="lyx-ws-transport"
      // Floating, so it carries a full border and a shadow rather than the single
      // top border a flush bar had. The shadow is what separates it from whatever
      // scrolls underneath.
      style={{
        background: c.surface1,
        border: `1px solid ${previewMode ? c.accent : c.borderStrong}`,
        boxShadow: previewMode
          ? `0 0 0 1px ${c.accentSoft}, 0 6px 24px rgba(0, 0, 0, 0.45), 0 1px 2px rgba(0, 0, 0, 0.3)`
          : '0 6px 24px rgba(0, 0, 0, 0.45), 0 1px 2px rgba(0, 0, 0, 0.3)',
      }}
    >
      {/* ---- transport controls ---- */}
      <div className="lyx-ws-transport-controls">
        {onTogglePreviewMode && (
          <button
            type="button"
            onClick={onTogglePreviewMode}
            aria-pressed={previewMode}
            aria-label={previewMode ? 'Switch transport to the head song' : 'Switch transport to preview mode'}
            className="lyx-ws-transport-mode"
            style={{
              border: `1px solid ${previewMode ? c.accentBorder : c.border}`,
              background: previewMode ? c.accentSoft : c.surface2,
              color: previewMode ? c.accent : c.text2,
            }}
          >
            {previewLabel ?? 'Preview mode'}
          </button>
        )}
        <Tooltip label="Previous track">
          <IconButton
            label="Previous track"
            nativeTitle={false}
            onClick={onPrev}
            disabled={!onPrev}
            style={{ width: 30, height: 30, borderRadius: 8 }}
          >
            <SkipBack size={15} color={c.text2} />
          </IconButton>
        </Tooltip>

        <button
          type="button"
          onClick={previewLoading ? undefined : onTogglePlay}
          aria-label={previewLoading ? 'Loading preview' : playing ? 'Pause' : 'Play'}
          aria-busy={previewLoading || undefined}
          disabled={previewLoading}
          style={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            background: c.accentFill,
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: previewLoading ? 'progress' : 'pointer',
            flexShrink: 0,
            opacity: previewLoading ? 0.9 : 1,
          }}
        >
          {previewLoading ? (
            <Loader2 size={15} color="#1A1300" className="lyx-spin" />
          ) : playing ? (
            <Pause size={15} color="#1A1300" />
          ) : (
            // The 1px nudge optically centres a triangle in a circle.
            <Play size={15} color="#1A1300" style={{ marginLeft: 1 }} />
          )}
        </button>

        <Tooltip label="Next track">
          <IconButton
            label="Next track"
            nativeTitle={false}
            onClick={onNext}
            disabled={!onNext}
            style={{ width: 30, height: 30, borderRadius: 8 }}
          >
            <SkipForward size={15} color={c.text2} />
          </IconButton>
        </Tooltip>
      </div>

      {/* ---- position: waveform over the scrubber, with the clock either side ----
          The waveform sits on the transport line rather than above it as its own
          block, which is what stops it reading as a debug graph. */}
      <div className="lyx-ws-transport-position">
        <span className="lyx-ws-time">{formatTime(pos)}</span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="lyx-ws-transport-wave">
            <Waveform heights={seededHeights(song.id, 48)} playing={playing} fill />
          </div>
          {/* Seeking works whether or not the track is playing, and does not start
              it: someone lining up a position before pressing play is doing exactly
              what a transport is for. `pos` rather than `elapsed`, so the bar shows
              zero for a song that is not the active one — the same rule the clock
              beside it follows.

              `playing` here and not `active`: the prop only governs the transition
              that smooths the tick, and a paused bar has no tick to smooth. `speed` sets
              how long that transition is — one tick — so the fill keeps pace with the
              clock at 1.5× and 2×. */}
          <SeekBar
            value={pos}
            duration={totalSec}
            onSeek={onSeek}
            playing={playing}
            speed={speed}
            label={`Seek within ${song.title}`}
            style={{ marginTop: 2 }}
          />
        </div>

        <span className="lyx-ws-time">{totalLabel}</span>
      </div>

      {/* ---- output: repeat, volume, speed, fullscreen ---- */}
      <div className="lyx-ws-transport-output">
        <Tooltip label="Repeat this song">
          <IconButton
            label="Repeat"
            nativeTitle={false}
            pressed={repeat}
            onClick={onToggleRepeat}
            style={{ width: 30, height: 30, borderRadius: 8 }}
          >
            <Repeat size={15} color={repeat ? c.accent : c.text2} />
          </IconButton>
        </Tooltip>

        <span className="lyx-ws-volume">
          {/*
            The speaker was a decorative <Volume2 /> sitting beside the slider —
            the icon that every player in existence has trained people to click,
            attached to nothing. It is a button now.

            `pressed` gives it aria-pressed, so the state is announced rather than
            being carried only by which glyph is drawn; the label flips with it so
            it always names the action the next click performs, which is what a
            screen reader reads out. Being a real <button> is also all the keyboard
            support it needs — Enter and Space are native.
          */}
          <Tooltip label="Mute / unmute">
            <IconButton
              label={muted ? 'Unmute' : 'Mute'}
              nativeTitle={false}
              pressed={muted}
              onClick={onToggleMute}
              style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0 }}
            >
              {/* A crossed-out speaker, in the accent, so muted is legible from the
                  shape and not from colour alone. */}
              {muted ? (
                <VolumeX size={14} color={c.accent} />
              ) : (
                <Volume2 size={14} color={c.text2} />
              )}
            </IconButton>
          </Tooltip>
          <input
            type="range"
            min={0}
            max={100}
            // The slider keeps showing the chosen level while muted rather than
            // dropping to zero: the level was not changed, and a control that
            // sprang back to the far left and then returned would read as having
            // been reset. What changes is the fill colour — see accentColor.
            value={volume}
            onChange={(e) => onSetVolume(Number(e.target.value))}
            aria-label="Volume"
            /*
              The width lives in CSS, not here. It was `width: 74` inline, and an inline
              style prop outranks a media query — so on a 360px phone the slider held
              its desktop width and forced the output group onto a line of its own that
              it then overflowed. The class lets it narrow with the bar.
            */
            className="lyx-ws-volume-slider"
            style={{
              // Greyed while muted, so the slider itself shows that its level is
              // not currently being heard.
              accentColor: muted ? c.text3 : c.accentFill,
            }}
          />
        </span>

        <Tooltip label="Playback speed">
          <select
            value={speed}
            onChange={(e) => onSetSpeed(Number(e.target.value))}
            aria-label="Playback speed"
            className="lyx-ws-speed"
            style={{
              background: c.surface2,
              border: `1px solid ${c.border}`,
              color: c.text2,
            }}
          >
            {PLAYBACK_SPEEDS.map((sp) => (
              <option key={sp} value={sp}>
                {sp}×
              </option>
            ))}
          </select>
        </Tooltip>

        {onOpenFullscreen && (
          <Tooltip label="Open full-screen player">
            <IconButton
              label="Open full-screen player"
              nativeTitle={false}
              onClick={onOpenFullscreen}
              style={{ width: 30, height: 30, borderRadius: 8 }}
            >
              <Maximize2 size={15} color={c.text2} />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
